<template>
  <div class="h-auto overflow-auto flex flex-col">
    <woot-modal-header
      :header-title="$t('CAMPAIGN.ADD.TITLE')"
      :header-content="$t('CAMPAIGN.ADD.DESC')"
    />
    <form class="flex flex-col w-full" @submit.prevent="addCampaign">
      <div class="w-full">
        <woot-input
          v-model="title"
          :label="$t('CAMPAIGN.ADD.FORM.TITLE.LABEL')"
          type="text"
          :class="{ error: $v.title.$error }"
          :error="$v.title.$error ? $t('CAMPAIGN.ADD.FORM.TITLE.ERROR') : ''"
          :placeholder="$t('CAMPAIGN.ADD.FORM.TITLE.PLACEHOLDER')"
          @blur="$v.title.$touch"
        />

        <label v-if="isOngoingType" class="editor-wrap">
          {{ $t('CAMPAIGN.ADD.FORM.MESSAGE.LABEL') }}
          <woot-message-editor
            v-model="message"
            class="message-editor"
            :class="{ editor_warning: $v.message.$error }"
            :placeholder="$t('CAMPAIGN.ADD.FORM.MESSAGE.PLACEHOLDER')"
            @blur="$v.message.$touch"
          />
          <span v-if="$v.message.$error" class="editor-warning__message">
            {{ $t('CAMPAIGN.ADD.FORM.MESSAGE.ERROR') }}
          </span>
        </label>


        <div
          v-if="isWhatsappType || isEmailCampaignType"
          class="form-group campaign-multiselect-field"
          :class="{ error: $v.selectedMacro.$error }"
        >
          <label>
            {{ $t('CAMPAIGN.ADD.FORM.MACRO.LABEL') }}
          </label>
          <multiselect
            v-model="selectedMacroObject"
            :options="macros"
            track-by="id"
            label="name"
            :placeholder="$t('CAMPAIGN.ADD.FORM.MACRO.PLACEHOLDER')"
            :allow-empty="true"
            :close-on-select="true"
            @input="onMacroInput"
          />
          <span v-if="$v.selectedMacro.$error" class="message">
            {{ $t('CAMPAIGN.ADD.FORM.MACRO.ERROR') }}
          </span>
        </div>

        <div
          class="form-group campaign-multiselect-field"
          :class="{ error: $v.selectedInbox.$error }"
        >
          <label>
            {{ $t('CAMPAIGN.ADD.FORM.INBOX.LABEL') }}
          </label>
          <multiselect
            v-model="selectedInboxObject"
            :options="inboxes"
            track-by="id"
            label="name"
            :placeholder="$t('CAMPAIGN.ADD.FORM.INBOX.PLACEHOLDER')"
            :allow-empty="true"
            :close-on-select="true"
            @input="onChangeInbox"
          />
          <span v-if="$v.selectedInbox.$error" class="message">
            {{ $t('CAMPAIGN.ADD.FORM.INBOX.ERROR') }}
          </span>
        </div>

        <div
          v-if="showEmailOutboundProvider"
          class="form-group campaign-multiselect-field campaign-outbound-provider mb-6"
        >
          <label>{{ $t('CAMPAIGN.ADD.FORM.OUTBOUND_PROVIDER.LABEL') }}</label>
          <multiselect
            :value="selectedEmailOutboundOption"
            :options="emailOutboundProviderOptions"
            label="label"
            track-by="value"
            :allow-empty="false"
            :searchable="false"
            :close-on-select="true"
            @input="onEmailOutboundProviderInput"
          />
        </div>

        <div
          v-if="isOnOffType || isWhatsappType || isEmailCampaignType"
          class="multiselect-wrap--small campaign-audience-multiselect min-w-0 w-full"
          :class="{ error: $v.selectedAudience.$error }"
        >
        <div class="flex justify-start items-center gap-2 mb-1">
          <label>
            {{ $t('CAMPAIGN.ADD.FORM.AUDIENCE.LABEL') }}
          </label>
          <woot-button class="!bg-slate-200 dark:!bg-slate-700" v-if="(isWhatsappType || isEmailCampaignType) && !importSelection" variant="clear" size="small" @click.prevent="onToggleImport">
            {{ $t('IMPORT_CONTACTS.TITLE') }}
          </woot-button>
          <woot-button class="!bg-slate-200 dark:!bg-slate-700" v-if="(isWhatsappType || isEmailCampaignType) && !importSelection" variant="clear" size="small" @click.prevent="onToggleFolder">
            {{ $t('CAMPAIGN.ADD.FORM.SELECT_FOLDER') }}
          </woot-button>
          </div>
          <multiselect
            v-if="!importSelection && !showFolderSelection"
            v-model="selectedAudience"
            :options="audienceList"
            track-by="id"
            label="title"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :hide-selected="true"
            :placeholder="$t('FORMS.MULTISELECT.SELECT')"
            selected-label
            :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            deselect-label=""
            @blur="$v.selectedAudience.$touch"
            @select="onAudienceInput"
            @input="onAudienceInput"
          ></multiselect>
          <div
            v-else-if="(isWhatsappType || isEmailCampaignType) && showFolderSelection"
            class="flex items-center gap-2 mb-4 campaign-folder-row"
          >
            <div class="form-group campaign-multiselect-field campaign-folder-multiselect flex-1 min-w-0 mb-0">
              <multiselect
                :value="selectedFolderObject"
                :options="folders"
                label="name"
                track-by="id"
                :placeholder="$t('CAMPAIGN.ADD.FORM.AUDIENCE.FOLDER_PLACEHOLDER')"
                :allow-empty="true"
                :searchable="folders.length > 6"
                :close-on-select="true"
                @input="onFolderMultiselectInput"
              />
            </div>
            <woot-button
              variant="clear"
              size="small"
              class="shrink-0 self-center"
              @click.prevent="clearFolder"
            >
              Clear
            </woot-button>
          </div>
          <div v-else class="flex items-center gap-2">
            <input
              class="mb-0 flex-1"
              type="text"
              :value="importFileName"
              readonly
            />
            <woot-button variant="clear" size="small" @click.prevent="onToggleImport">
              Change
            </woot-button>
            <woot-button variant="clear" size="small" @click.prevent="clearImport">
              Clear
            </woot-button>
          </div>

          <span v-if="$v.selectedAudience.$error && !importSelection" class="message">
            {{ $t('CAMPAIGN.ADD.FORM.AUDIENCE.ERROR') }}
          </span>
        </div>
        <label
          v-if="isOngoingType"
          :class="{ error: $v.selectedSender.$error }"
        >
          {{ $t('CAMPAIGN.ADD.FORM.SENT_BY.LABEL') }}
          <select v-model="selectedSender">
            <option
              v-for="sender in sendersAndBotList"
              :key="sender.name"
              :value="sender.id"
            >
              {{ sender.name }}
            </option>
          </select>
          <span v-if="$v.selectedSender.$error" class="message">
            {{ $t('CAMPAIGN.ADD.FORM.SENT_BY.ERROR') }}
          </span>
        </label>

        <div
          v-if="isOnOffType || isWhatsappType || isEmailCampaignType"
          :class="{ error: $v.scheduledAt.$error }"
        >
          {{ $t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.LABEL') }}
          <woot-date-time-picker
            :value="scheduledAt"
            :confirm-text="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.CONFIRM')"
            :placeholder="$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.PLACEHOLDER')"
            @change="onChange"
          />
          <span v-if="$v.scheduledAt.$error" class="message">
            {{ scheduledAtErrorMessage }}
          </span>
        </div>

        <woot-input
          v-if="isOngoingType"
          v-model="endPoint"
          :label="$t('CAMPAIGN.ADD.FORM.END_POINT.LABEL')"
          type="text"
          :class="{ error: $v.endPoint.$error }"
          :error="
            $v.endPoint.$error ? $t('CAMPAIGN.ADD.FORM.END_POINT.ERROR') : ''
          "
          :placeholder="$t('CAMPAIGN.ADD.FORM.END_POINT.PLACEHOLDER')"
          @blur="$v.endPoint.$touch"
        />
        <woot-input
          v-if="isOngoingType"
          v-model="timeOnPage"
          :label="$t('CAMPAIGN.ADD.FORM.TIME_ON_PAGE.LABEL')"
          type="text"
          :class="{ error: $v.timeOnPage.$error }"
          :error="
            $v.timeOnPage.$error
              ? $t('CAMPAIGN.ADD.FORM.TIME_ON_PAGE.ERROR')
              : ''
          "
          :placeholder="$t('CAMPAIGN.ADD.FORM.TIME_ON_PAGE.PLACEHOLDER')"
          @blur="$v.timeOnPage.$touch"
        />
        <label v-if="isOngoingType">
          <input
            v-model="enabled"
            type="checkbox"
            value="enabled"
            name="enabled"
          />
          {{ $t('CAMPAIGN.ADD.FORM.ENABLED') }}
        </label>
        <label v-if="isOngoingType">
          <input
            v-model="triggerOnlyDuringBusinessHours"
            type="checkbox"
            value="triggerOnlyDuringBusinessHours"
            name="triggerOnlyDuringBusinessHours"
          />
          {{ $t('CAMPAIGN.ADD.FORM.TRIGGER_ONLY_BUSINESS_HOURS') }}
        </label>
      </div>

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
        <woot-button 
          :is-loading="uiFlags.isCreating"
          :is-disabled="isFormInvalid"
        >
          {{ $t('CAMPAIGN.ADD.CREATE_BUTTON_TEXT') }}
        </woot-button>
        <woot-button variant="clear" @click.prevent="onClose">
          {{ $t('CAMPAIGN.ADD.CANCEL_BUTTON_TEXT') }}
        </woot-button>
      </div>
    </form>
    <woot-modal :show.sync="showImportModal" :on-close="onToggleImport">
      <campaign-import-contacts
        v-if="showImportModal"
        :importSelection="importSelection"
        :on-close="onToggleImport"
        @selected-import="onSelectedImport"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import campaignMixin from 'shared/mixins/campaignMixin';
import WootDateTimePicker from 'dashboard/components/ui/DateTimePicker.vue';
import { URLPattern } from 'urlpattern-polyfill';
import { CAMPAIGNS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import CampaignImportContacts from './CampaignImportContacts.vue';
import CustomViewsAPI from 'dashboard/api/customViews';


export default {
  components: {
    WootDateTimePicker,
    WootMessageEditor,
    CampaignImportContacts
  },

  mixins: [alertMixin, campaignMixin],
  data() {
    return {
      title: '',
      message: '',
      selectedSender: 0,
      selectedInbox: null,
      selectedMacro: null,
      endPoint: '',
      timeOnPage: 10,
      show: true,
      enabled: true,
      triggerOnlyDuringBusinessHours: false,
      scheduledAt: null,
      selectedAudience: [],
      senderList: [],
      data: [],
      selectedInboxObject: null,
      selectedMacroObject: null,
      showImportModal: false,
      importSelection: '', // { file }
      folders: [],
      selectedFolderName: '',
      modeError: '',
      showFolderSelection: false,
      emailOutboundProvider: 'default',
      emailOutboundProviderOptions: [
        { value: 'default', label: 'Default (SMTP / Microsoft)' },
        { value: 'sendgrid', label: 'SendGrid' },
      ],
    };
  },

  validations() {
    const baseValidations = {
      title: {
        required,
      },
      selectedInbox: {
        required,
      },
    };
    const macroValidations =
      this.isWhatsappType || this.isEmailCampaignType
        ? {
            selectedMacro: {
              required,
            },
          }
        : {
            selectedMacro: {
              required: false,
            },
          };
    if (this.isOngoingType) {
      return {
        ...baseValidations,
        selectedMacro: {
          required: false,
        },
        selectedSender: {
          required,
        },
        message: {
        required,
      },
        endPoint: {
          required,
          shouldBeAValidURLPattern(value) {
            try {
              // eslint-disable-next-line
              new URLPattern(value);
              return true;
            } catch (error) {
              return false;
            }
          },
          shouldStartWithHTTP(value) {
            if (value) {
              return (
                value.startsWith('https://') || value.startsWith('http://')
              );
            }
            return false;
          },
        },
        timeOnPage: {
          required,
        },
      };
    }
    return {
      ...baseValidations,
      ...macroValidations,
      scheduledAt: {
        required,
      },
      selectedAudience: {
        isEmpty() {
          if (!this.isWhatsappType && !this.isEmailCampaignType) {
            return !!this.selectedAudience.length;
          }
          // WhatsApp / Email: exactly one of audience, import file, or folder is required
          const audienceChosen = this.selectedAudience.length > 0;
          const fileChosen = !!this.importSelection;
          const folderChosen = !!this.selectedFolderName;
          const chosenCount = [audienceChosen, fileChosen, folderChosen].filter(Boolean).length;
          this.modeError =
            chosenCount === 0
              ? 'Select audience or import file or folder'
              : chosenCount > 1
                ? 'Choose only one: audience or file or folder'
                : '';
          return chosenCount === 1;
        },
      },
      scheduledAt: {
        isValidScheduledTime(value) {
          // Check if scheduled time is provided and is in the future
          if (!value) return false;
          const scheduledDate = new Date(value);
          const minTime = new Date(Date.now() + 3 * 60 * 1000); // 3 minutes from now
          return scheduledDate >= minTime;
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',
      audienceList: 'labels/getLabels'
    }),
    
    // Check if form is invalid to disable submit button
    isFormInvalid() {
      // Basic required fields
      if (!this.title || !this.selectedInbox) return true;
      
      // For ongoing type
      if (this.isOngoingType) {
        if (!this.message || !this.endPoint) return true;
        return false;
      }
      
      // For one-off and whatsapp campaigns, scheduled time is required
      if (this.isOnOffType || this.isWhatsappType || this.isEmailCampaignType) {
        if (!this.scheduledAt) return true;
        
        // Validate scheduled time is in the future
        const scheduledDate = new Date(this.scheduledAt);
        if (scheduledDate <= new Date()) return true;
      }
      
      // For WhatsApp campaigns, validate audience selection
      if (this.isWhatsappType || this.isEmailCampaignType) {
        if (this.selectedMacro == null || this.selectedMacro === '') return true;
        const hasAudience = this.selectedAudience.length > 0;
        const hasImport = !!this.importSelection;
        const hasFolder = !!this.selectedFolderName;
        const chosenCount = [hasAudience, hasImport, hasFolder].filter(Boolean).length;
        if (chosenCount !== 1) return true;
      } else if (this.isOnOffType) {
        // For one-off campaigns, audience is required
        if (!this.selectedAudience.length) return true;
      }
      
      return false;
    },
    scheduledAtErrorMessage() {
      if (this.sendNow) return '';
      
      // If no time is selected, show "required" error
      if (!this.scheduledAt) {
        return this.$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.ERROR');
      }
      
      // If time is selected but in the past or too soon, show "invalid time" error
      const scheduledDate = new Date(this.scheduledAt);
      const minTime = new Date(Date.now() + 3 * 60 * 1000); // 3 minutes from now
      if (scheduledDate < minTime) {
        return this.$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.INVALID_TIME_ERROR');
      }
      
      return this.$t('CAMPAIGN.ADD.FORM.SCHEDULED_AT.ERROR');
    },
    inboxes() {
      if (this.isOngoingType) {
        return this.$store.getters['inboxes/getWebsiteInboxes'];
      }
      if (this.isWhatsappType) {
        return this.$store.getters['inboxes/getWhatsappInboxes'];
      }
      if (this.isEmailCampaignType) {
        return this.$store.getters['inboxes/getEmailInboxes'];
      }
      return this.$store.getters['inboxes/getSMSInboxes'];
    },

    macros() {
      const all = this.$store.getters['macros/getMacros'] || [];
      if (this.isEmailCampaignType) {
        return all.filter(m =>
          (m.actions || []).some(a => a.action_name === 'email_template')
        );
      }
      return all;
    },

    sendersAndBotList() {
      return [
        {
          id: 0,
          name: 'Bot',
        },
        ...this.senderList,
      ];
    },
    importFileName() {
      const f = this.importSelection;
      if (!f) return '';
      // File object or string fallback
      return typeof f === 'object' && 'name' in f ? f.name : String(f);
    },
    showEmailOutboundProvider() {
      if (!this.isEmailCampaignType || !this.selectedInboxObject) return false;
      const inbox = this.selectedInboxObject;
      const hasDefault =
        inbox.smtp_enabled ||
        (inbox.provider === 'microsoft' && inbox.imap_enabled);
      // API exposes sendgrid_outbound_configured for agents (no API key in JSON); admins may still have provider_config.
      const hasSendgrid =
        inbox.sendgrid_outbound_configured === true ||
        !!(inbox.provider_config?.sendgrid?.enabled && inbox.provider_config?.sendgrid?.api_key);
      return hasDefault && hasSendgrid;
    },
    selectedEmailOutboundOption() {
      return (
        this.emailOutboundProviderOptions.find(
          o => o.value === this.emailOutboundProvider
        ) || this.emailOutboundProviderOptions[0]
      );
    },
    selectedFolderObject() {
      if (!this.selectedFolderName || !this.folders.length) return null;
      return (
        this.folders.find(f => f.name === this.selectedFolderName) || null
      );
    },
  },
  mounted() {
    this.$track(CAMPAIGNS_EVENTS.OPEN_NEW_CAMPAIGN_MODAL, {
      type: this.campaignType,
    });
    if (this.isWhatsappType || this.isEmailCampaignType) {
      this.fetchFolders();
    }
    this.getAllMacro();
  },
  methods: {
    onAudienceInput() {
      if (this.selectedAudience.length) {
        this.importSelection = '';
        this.selectedFolderName = '';
        this.showFolderSelection = false;
      }
    },
    onToggleImport() {
      this.showImportModal = !this.showImportModal;
      if (this.showImportModal) {
        this.selectedAudience = [];
        this.selectedFolderName = '';
        this.showFolderSelection = false;
      }
    },
    onToggleFolder() {
      this.showFolderSelection = !this.showFolderSelection;
      if (this.showFolderSelection) {
        this.selectedAudience = [];
        this.importSelection = '';
      }
    },
    onSelectedImport(payload) {
      console.log("payload", payload);
      this.importSelection = payload.file || null; // { file }
      if (this.importSelection) {
        this.selectedAudience = [];
        this.selectedFolderName = '';
        this.showFolderSelection = false;
      }
    },
    onChangeFolder() {
      if (this.selectedFolderName) {
        this.selectedAudience = [];
        this.importSelection = '';
      }
    },
    onEmailOutboundProviderInput(option) {
      this.emailOutboundProvider = option && option.value != null ? option.value : 'default';
    },
    onFolderMultiselectInput(folder) {
      this.selectedFolderName = folder && folder.name != null ? folder.name : '';
      this.onChangeFolder();
    },
    clearFolder() {
      this.selectedFolderName = '';
      this.showFolderSelection = false;
    },
    onClose() {
      this.$emit('on-close');
    },

    async getAllMacro(){
      await this.$store.dispatch('macros/get');
    },
    onChange(value) {
      this.scheduledAt = value;
    },
    onMacroInput(option) {
      this.selectedMacro = option && option.id != null ? option.id : null;
      this.$v.selectedMacro?.$touch();
      this.onChangeMacro();
    },
    async onChangeInbox() {
      const inbox = this.selectedInboxObject;
      if (!inbox || inbox.id == null) {
        this.selectedInbox = null;
        this.emailOutboundProvider = 'default';
        return;
      }
      this.selectedInbox = inbox.id;
      this.emailOutboundProvider = 'default';
      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.selectedInbox,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        this.senderList = inboxMembers;
      } catch (error) {
        const errorMessage =
          error?.response?.message || this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    async onChangeMacro() {
      try {
        // const response= await this.$store.dispatch('macros/getSingleMacro', {
        //   id: this.selectedMacro,
        // });
        // const {
        //   data: { payload },
        // } = response;

        // this.senderList = payload;
      } catch (error) {
        this.showAlert(this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE'));
      }
    },
    getCampaignDetails() {
      let campaignDetails = null;
      if (this.isOngoingType) {
        campaignDetails = {
          title: this.title,
          message: this.message,
          inbox_id: this.selectedInbox,
          macro_id: this.selectedMacro,
          sender_id: this.selectedSender || null,
          enabled: this.enabled,
          trigger_only_during_business_hours:
            // eslint-disable-next-line prettier/prettier
            this.triggerOnlyDuringBusinessHours,
          trigger_rules: {
            url: this.endPoint,
            time_on_page: this.timeOnPage,
          },
        };
      } else if (this.isWhatsappType || this.isEmailCampaignType) {
        // const audience = this.selectedAudience.map(item => {
        //   return {
        //     id: item.id,
        //     type: 'Label',
        //   };
        // });
        // campaignDetails = {
        //   title: this.title,
        //   message: this.message,
        //   inbox_id: this.selectedInbox,
        //   macro_id: this.selectedMacro,
        //   sender_id: this.selectedSender || null,
        //   enabled: this.enabled,
        //   scheduled_at: this.scheduledAt,
        //   trigger_only_during_business_hours:
        //     // eslint-disable-next-line prettier/prettier
        //     this.triggerOnlyDuringBusinessHours,
        //   trigger_rules: {
        //     url: this.endPoint,
        //     time_on_page: this.timeOnPage,
        //   },
        //   audience,

        // };

        const audience = this.selectedAudience.length
          ? this.selectedAudience.map(item => ({ id: item.id, type: 'Label' }))
          : [];

        const formData = new FormData();
        formData.append('campaign[title]', this.title);
        formData.append('campaign[message]', this.message);
        formData.append('campaign[inbox_id]', this.selectedInbox);

        if (this.selectedMacro != null) {
          formData.append('campaign[macro_id]', this.selectedMacro);
        }

        formData.append('campaign[sender_id]', this.selectedSender || '');

        formData.append('campaign[enabled]', this.enabled);

        if (this.scheduledAt != null) {
          formData.append('campaign[scheduled_at]', this.scheduledAt);
        }

        if (this.importSelection) {
          formData.append('campaign[import_file]', this.importSelection);
        }

        formData.append(
          'campaign[trigger_only_during_business_hours]',
          this.triggerOnlyDuringBusinessHours
        );

        formData.append('campaign[trigger_rules][url]', this.endPoint || '');
        formData.append('campaign[trigger_rules][time_on_page]', this.timeOnPage || '');
        if (this.isEmailCampaignType && this.showEmailOutboundProvider) {
          const outbound =
            this.emailOutboundProvider === 'sendgrid' ? 'sendgrid' : 'default';
          formData.append('campaign[trigger_rules][outbound_provider]', outbound);
          if (outbound === 'sendgrid') {
            formData.append('campaign[outbound_email_provider]', 'sendgrid');
          }
        }
        if (this.selectedFolderName) {
          formData.append('campaign[trigger_rules][folder_name]', this.selectedFolderName);

          const folderObj = this.folders.find(f => f.name === this.selectedFolderName);
          if (folderObj && folderObj.query) {
            formData.append(
              'campaign[trigger_rules][contact_filter_payload]',
              JSON.stringify(folderObj.query)
            );
          }    
        }
        if (audience.length) {
          audience.forEach((item) => {
            formData.append(`campaign[audience][][id]`, item.id);
            formData.append(`campaign[audience][][type]`, item.type);
          });
        }
          return formData;
      } else {
        const audience = this.selectedAudience.map(item => {
          return {
            id: item.id,
            type: 'Label',
          };
        });
        campaignDetails = {
          title: this.title,
          message: this.message,
          inbox_id: this.selectedInbox,
          macro_id: this.selectedMacro,
          scheduled_at: this.scheduledAt,
          audience,
        };
      }
      return campaignDetails;
    },
    async addCampaign() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        const campaignDetails = this.getCampaignDetails();
        await this.$store.dispatch('campaigns/create', campaignDetails);

        // tracking this here instead of the store to track the type of campaign
        this.$track(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
          type: this.campaignType,
        });

        this.showAlert(this.$t('CAMPAIGN.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error?.response?.message || this.$t('CAMPAIGN.ADD.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    clearImport() {
      this.importSelection = '';
      // keep existing audience selections visible again
    },
    async fetchFolders() {
      try {
        const { data } = await CustomViewsAPI.getCustomViewsByFilterType(
          'contact'
        );
        this.folders = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error('Error in folder fetch');
      }
    },
  },
};
</script>
<style lang="scss" scoped>
:deep(.ProseMirror-woot-style) {

  height: 5rem;
}

.message-editor {
  @apply px-3;

  :deep {
    .ProseMirror-menubar {
      @apply rounded-tl-[4px];
    }
  }
}
::v-deep .multiselect__option:not(.multiselect__option--selected)::after {
  content: none !important;
}
::v-deep .multiselect .multiselect__content {
  max-width: none;
}

/* Match inbox + macro multiselects: full border, no clipped corners */
.campaign-multiselect-field {
  ::v-deep .multiselect {
    @apply mb-4 rounded-md;
    overflow: visible;
  }

  ::v-deep .multiselect__tags {
    @apply rounded-md;
    overflow: visible;
  }

  ::v-deep .multiselect__tags-wrap {
    overflow-x: visible;
  }

  ::v-deep .multiselect__placeholder {
    @apply ml-0 pl-1;
  }
}

/*
 * Audience multiselect: allow chips to wrap. Parent .multiselect-wrap--small sets a fixed
 * h-10 on .multiselect which clips wrapped rows; override height here (see ScheduleDemoModal).
 */
.campaign-audience-multiselect {
  ::v-deep .multiselect {
    @apply rounded-md;
    height: auto;
    min-height: 2.5rem;
    max-width: 100%;
    min-width: 0;
    overflow: visible;
  }

  ::v-deep .multiselect__tags {
    @apply rounded-md;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: flex-start;
    gap: 4px;
    height: auto;
    min-height: 2.5rem;
    overflow: visible;
    min-width: 0;
    max-width: 100%;
    padding: 6px 40px 6px 8px;
  }

  ::v-deep .multiselect__tags-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    align-content: flex-start;
    gap: 4px;
    flex: 1 1 100%;
    min-width: 0;
    max-width: 100%;
  }

  ::v-deep .multiselect__tag {
    flex-shrink: 0;
    max-width: 120px;
    margin: 0 !important;
    padding: 4px 26px 4px 10px !important;
    font-size: 13px;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .multiselect__tag span {
    max-width: 100% !important;
  }

  ::v-deep .multiselect__tag-icon {
    line-height: 1.4;
    width: 20px;
    text-align: center;
    background: transparent !important;
  }

  ::v-deep .multiselect__tag-icon::after {
    content: '\00d7' !important;
    font-size: 14px !important;
  }

  ::v-deep .multiselect__strong {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin: 0 4px;
    padding: 4px 10px;
    font-size: 13px;
    font-weight: 600;
    color: white;
    background-color: #1f93ff;
    border-radius: 12px;
    min-width: 32px;
    height: 26px;
    line-height: 1;
  }

  ::v-deep .multiselect__select {
    top: 0;
    right: 0;
    bottom: 0;
    height: auto;
    min-height: 2.5rem;
  }

  ::v-deep .multiselect__placeholder {
    @apply ml-0 pl-1;
  }
}

.campaign-multiselect-field,
.campaign-audience-multiselect {
  ::v-deep .multiselect__input[style*='width: 0'],
  ::v-deep .multiselect__input[style*='width:0'] {
    display: none !important;
  }

  ::v-deep .multiselect--active .multiselect__input {
    display: inline-block !important;
  }
}

/* Dropdown panels above modal content / other fields */
.campaign-multiselect-field,
.campaign-audience-multiselect,
.campaign-folder-multiselect {
  ::v-deep .multiselect__content-wrapper {
    @apply z-[100];
  }
}

.campaign-folder-multiselect {
  ::v-deep .multiselect:not(.no-margin) {
    @apply mb-0;
  }

  ::v-deep .multiselect__tags {
    @apply min-h-[2.875rem];
  }
}

</style>
